// src/admin/components/UserTable.jsx
import React, { useState } from 'react';
import { FiSearch } from 'react-icons/fi';
import '../../styles/UserTable.css';

const UserTable = ({ 
  users = [], 
  tenants = [], 
  currentUser = {}, 
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Format role display
  const formatRole = (role) => {
    if (!role) return 'Unknown Role';
    const rolesMap = {
      'super-admin': 'Super Admin',
      'global_admin': 'Global Admin',
      'location_admin': 'Location Admin',
      'location_user': 'Location User'
    };
    return rolesMap[role] || role;
  };

  // Get tenant name by ID
  const getTenantName = (tenantId) => {
    if (!tenantId || tenantId === 'system') return 'System';
    const tenant = tenants.find(t => t.id === tenantId);
    return tenant ? tenant.firstName : 'Unknown Tenant';
  };

  // Filter users based on search term
  const filteredUsers = users.filter(user => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (user.firstName && user.firstName.toLowerCase().includes(searchLower)) ||
      (user.lastName && user.lastName.toLowerCase().includes(searchLower)) ||
      (user.email && user.email.toLowerCase().includes(searchLower)) ||
      (user.role && formatRole(user.role).toLowerCase().includes(searchLower))
    );
  });

  // Group users by tenant and role
  const groupUsersByTenant = () => {
    const grouped = {};
    
    filteredUsers.forEach(user => {
      if (!grouped[user.tenantId]) {
        grouped[user.tenantId] = {
          name: getTenantName(user.tenantId),
          globalAdmins: [],
          otherUsers: []
        };
      }

      if (user.role === 'global_admin') {
        grouped[user.tenantId].globalAdmins.push(user);
      } else {
        grouped[user.tenantId].otherUsers.push(user);
      }
    });

    return grouped;
  };

  const groupedUsers = groupUsersByTenant();

  if (!users || !tenants) {
    return <div className="loading-message">Loading user data...</div>;
  }

  return (
    <div className="user-table-container">
      <div className="search-bar">
        <FiSearch className="search-icon" />
        <input
          type="text"
          placeholder="Search users..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {Object.keys(groupedUsers).length === 0 ? (
        <div className="no-results">loading...</div>
      ) : (
        <div className="tenant-groups">
          {Object.entries(groupedUsers).map(([tenantId, { globalAdmins, otherUsers }]) => (
            <div key={tenantId} className="tenant-group">
              <table className="user-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Global Admins */}
                  {globalAdmins.map(user => (
                    <tr key={user.id} className="global-admin-row">
                      <td data-label="Name">{user.firstName || ''} {user.lastName || ''}</td>
                      <td data-label="Email">{user.email || 'No email'}</td>
                      <td data-label="Role">{formatRole(user.role)}</td>
                      <td className={`status-${user.isActive ? 'active' : 'inactive'}`}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </td>
                    </tr>
                  ))}

                  {/* Other Users (indented) */}
                  {otherUsers.map(user => (
                    <tr key={user.id} className="user-row">
                      <td className="indented-cell">{user.firstName || ''} {user.lastName || ''}</td>
                      <td>{user.email || 'No email'}</td>
                      <td>{formatRole(user.role)}</td>
                      <td className={`status-${user.isActive ? 'active' : 'inactive'}`}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UserTable;